/**
 * Notification REST service.
 *
 * Wraps the five backend endpoints under /api/v1/notifications:
 *   - list (paginated, with is_read / is_dismissed filters)
 *   - unread count (for the bell badge)
 *   - mark a single notification as read
 *   - mark all as read
 *   - dismiss (soft delete)
 *
 * Live push via AppSync subscription is wired separately in
 * hooks/useNotifications.ts once the AppSync deploy is available; this file
 * only deals with REST. The catch-up flow (list + unread-count on reconnect)
 * uses these calls.
 */

import { apiRequest } from "./api.client";

export interface Notification {
  notification_id: string;
  user_id: string | null;     // null = clinic-wide broadcast
  clinic_id: string;
  event_type: string;
  title: string;
  body: string;
  link: string | null;
  payload: Record<string, unknown>;
  is_read: boolean;
  is_dismissed: boolean;
  created_at: string;         // ISO datetime
  read_at: string | null;
}

export interface NotificationListResponse {
  items: Notification[];
  next_cursor: string | null;
}

export interface UnreadCountResponse {
  count: number;
}

interface ListOptions {
  is_read?: boolean;
  is_dismissed?: boolean;
  limit?: number;
  cursor?: string;
}

function buildQuery(options: ListOptions): string {
  const params = new URLSearchParams();
  if (options.is_read !== undefined) params.set("is_read", String(options.is_read));
  if (options.is_dismissed !== undefined) params.set("is_dismissed", String(options.is_dismissed));
  if (options.limit !== undefined) params.set("limit", String(options.limit));
  if (options.cursor) params.set("cursor", options.cursor);
  const qs = params.toString();
  return qs ? `?${qs}` : "";
}

export const notificationService = {
  async list(options: ListOptions = {}): Promise<NotificationListResponse> {
    return apiRequest<NotificationListResponse>(
      `/notifications${buildQuery(options)}`,
    );
  },

  async unreadCount(): Promise<UnreadCountResponse> {
    return apiRequest<UnreadCountResponse>("/notifications/unread-count");
  },

  async markRead(notificationId: string): Promise<void> {
    await apiRequest<void>(
      `/notifications/${notificationId}/read`,
      { method: "POST" },
    );
  },

  async markAllRead(): Promise<void> {
    await apiRequest<void>("/notifications/read-all", { method: "POST" });
  },

  async dismiss(notificationId: string): Promise<void> {
    await apiRequest<void>(
      `/notifications/${notificationId}`,
      { method: "DELETE" },
    );
  },

  async dismissAll(): Promise<void> {
    await apiRequest<void>("/notifications/dismiss-all", { method: "POST" });
  },
};
